<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo-->
      <b-link class="brand-logo">
        <h2 class="d-flex brand-text text-primary ml-1">
          {{ loginType }} Forest <br class="d-block d-md-none" />
          <span class="d-none d-md-flex">&nbsp;-&nbsp;</span>NTDC Portal
        </h2>
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" alt="Login V2" />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            <div>
              Welcome to <br />
              <span class="text-semibold-primary">{{ loginType }}</span>
              <span class="system-name">
                Forest <br />
                NTDC Portal
              </span>
              <span class="system-name--large-screen">
                Forest - NTDC Portal
              </span>
            </div>
          </b-card-title>
          <b-card-text class="mb-2">
            Please sign-in to your account
          </b-card-text>

          <!-- form -->
          <!-- Login External -->
          <template v-if="!isInternalLogin">
            <validation-observer ref="loginValidation">
              <b-form class="auth-login-form mt-2" @submit.prevent>
                <!-- email -->
                <b-form-group label="Username" label-for="login-email">
                  <validation-provider
                    #default="{ errors }"
                    name="Username"
                    rules="required"
                  >
                    <b-form-input
                      id="login-email"
                      v-model="userEmail"
                      :state="errors.length > 0 ? false : null"
                      name="login-email"
                      placeholder="Username"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- forgot password -->
                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="Password"
                    rules="required"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="login-password"
                        v-model="password"
                        :state="errors.length > 0 ? false : null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="login-password"
                        placeholder="············"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- submit buttons -->
                <b-button
                  class="mt-3"
                  type="submit"
                  variant="primary"
                  block
                  @click="validationForm"
                >
                  Sign in
                </b-button>
              </b-form>
            </validation-observer>
          </template>

          <!-- Login Internal (SSO) -->
          <template v-else>
            <b-button
              type="submit"
              variant="primary"
              block
              @click="goToLoginOidc"
            >
              Sign in SSO
            </b-button>
          </template>
        </b-col>
      </b-col>
      <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from "vee-validate"
import VuexyLogo from "@core/layouts/components/Logo.vue"
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
} from "bootstrap-vue"
import { required } from "@validations"
import { togglePasswordVisibility } from "@core/mixins/ui/forms"
import { authentication } from "@/api/api-module-iam"
import { getAllSiteList } from "@/api/api-module-data"
import { mapActions, mapGetters } from "vuex"
import mixins from "@/mixins"

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility, mixins],
  data() {
    return {
      status: "",
      password: "",
      userEmail: "",
      sideImg: require("@/assets/images/pages/login-v2-dark.svg"),
      // validation rulesimport store from '@/store/index'
      required,
    }
  },
  computed: {
    ...mapGetters("appConfig", ["getLayoutSkin"]),
    layoutSkin() {
      return this.getLayoutSkin
    },
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon"
    },
    imgUrl() {
      if (this.loginType === "Customer") {
        this.sideImg = require("@/assets/images/pages/reset-password-v2.svg")
        return this.sideImg
      }
      return this.sideImg
    },
    isInternalLogin() {
      return this.$route.fullPath.toLowerCase().includes("internal")
    },
    loginType() {
      return this.isInternalLogin ? "Internal" : "Customer"
    },
  },
  methods: {
    ...mapActions("appPermission", [
      "setAccessPermission",
      "saveAccessPermissionToLocalStorage",
      "setInternalAccessPermission",
      "saveInternalAccessPermissionToLocalStorage",
    ]),

    validationForm() {
      this.$refs.loginValidation.validate().then((success) => {
        if (success) this.login()
      })
    },

    async login() {
      this.showLoading(true)

      const loginData = {
        username: this.userEmail,
        password: this.password,
      }

      try {
        const userResponse = await authentication(loginData)
        const permissions = userResponse.data.permissions
        const userRole = userResponse.data.role.roleName.toLowerCase()

        permissions.login = true
        if (userRole === "admin") {
          permissions.admin = true
        }

        const isInvalidInternalPath =
          ["admin", "staff", "executive"].includes(userRole) &&
          !this.isInternalLogin
        const isInvalidCustomerPath =
          userRole === "customer" && this.isInternalLogin

        if (isInvalidInternalPath || isInvalidCustomerPath) {
          throw error
        }

        const permissionData = {
          token: userResponse.data.token,
          tokenExpireAt: userResponse.data.tokenExpireAt,
          userRole: userResponse.data.role.roleName,
          permissions: permissions,
          sitePermissions: userResponse.data.siteIds,
          departmentPermissions: userResponse.data.departmentIds,
          idcId: userResponse.data.firstName,
          username: userResponse.data.username,
          fullName:
            userRole === "customer"
              ? userResponse.data.lastName
              : userResponse.data.firstName + " " + userResponse.data.lastName,
        }

        this.setUserPermission(permissionData)

        this.isInternalLogin
          ? this.$router.push("/internal")
          : this.$router.push("/")
        this.toastSuccess("Login Successfully")
      } catch (error) {
        this.toastError("Invalid username or password")
      } finally {
        this.showLoading(false)
      }
    },

    async getAllSitePermission() {
      const response = await getAllSiteList()
      return response ? response.data.map((item) => item.siteId) : []
    },

    async setUserPermission(permissionData) {
      if (permissionData.userRole.toLowerCase() === "customer") {
        this.setAccessPermission(permissionData)
        this.saveAccessPermissionToLocalStorage(permissionData)
      }
    },

    async goToLoginOidc() {
      await this.$store.dispatch("authStore/loginOidc")
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
.system-name {
  display: inline;
}

.system-name--large-screen {
  display: none;
}

// XX-Large devices (Extra large desktops, 1530px and up)
@media screen and (min-width: 1530px) {
  .system-name {
    display: none;
  }

  .system-name--large-screen {
    display: inline;
  }
}
</style>
